import { createSelector } from '@reduxjs/toolkit';
import {
  AaasBillingMode,
  AaasPackage,
  AaasSubscriptionType,
  ActivePackageInfo,
  AdminTheme,
  AppState,
  ExternalSubscriptionObject,
  IntpWebsite,
  RootState,
  SubscriptionTypeEnum,
} from '@va/aaas/shared/types';
import { apiStatus, is3ASUnlimitedFreeTrialPlan } from '@va/constants';
import { selectStpConsumed, selectStpRates } from './stp-rates';

function getAppState(state: RootState): AppState {
  return state.get('app');
}

export const selectAllowUpgrade = (state: RootState) => state.get('app').allowUpgrade;
export const selectExternalWebsiteId = (state: RootState) => state.get('app').externalWebsiteId as string;

// Stable fallback reference: `intpWebsite` is missing outside AaaS, and a new `{}` per call would break memoization
const EMPTY_INTP_WEBSITE = {} as IntpWebsite;

export const selectIntpWebsite = (state: RootState) =>
  (state.get('app')?.intpWebsite as IntpWebsite) ?? EMPTY_INTP_WEBSITE;
export const selectExternalSubscription = (state: RootState) =>
  state.get('app')?.externalSubscription?.data as ExternalSubscriptionObject;
export const selectExternalSubscriptionStatus = (state: RootState) =>
  state.get('app')?.externalSubscription?.status as apiStatus;

const selectIsWebsiteSubscriptionModuleEnabled = createSelector(selectIntpWebsite, (website) => {
  return (
    website.billingMode === AaasBillingMode.PlatformManaged && website.subscriptionType === AaasSubscriptionType.Website
  );
});
const selectIsAccountSettingsModuleEnabled = createSelector(selectIntpWebsite, (website) => {
  return (
    website.billingMode === AaasBillingMode.PlatformManaged && website.subscriptionType === AaasSubscriptionType.Intpc
  );
});

export const AaasAppSelectors = {
  allowUpgrade: (state: RootState) => getAppState(state).allowUpgrade,
  externalWebsiteId: (state: RootState) => getAppState(state).externalWebsiteId,
  intpWebsite: (state: RootState) => getAppState(state).intpWebsite,
  selectIsWebsiteSubscriptionModuleEnabled,
  selectIsAccountSettingsModuleEnabled,
  isFirstLogin: (state: RootState) => getAppState(state).isFirstLogin,
  theme: (state: RootState) => getAppState(state).theme,
  dashboardType: (state: RootState) => getAppState(state).dashboardType,
};

export const selectApplicationName = createSelector(AaasAppSelectors.theme, (theme) => {
  if (theme?.applicationName) return theme.applicationName;
  return theme?.intpName ?? '';
});

export const selectActivePackageTranslatedName = (translate: (val: string) => string) => {
  return createSelector(selectActivePackageInfo, selectSubscriptionTouchpointsCount, (pkg, touchpointsCount) => {
    if (pkg?.name.toLowerCase() === 'free' && touchpointsCount === 0) {
      return translate('plan.activeSubscription.none');
    }

    if (pkg?.name.toLocaleLowerCase() === 'free') {
      return translate('aaas.planNames.free');
    }

    if (pkg && is3ASUnlimitedFreeTrialPlan(pkg.name)) {
      return translate('aaas.planNames.uft');
    }

    return pkg ? pkg.name : translate('plan.activeSubscription.none');
  });
};

export const selectActivePackageInfo = createSelector(
  selectIntpWebsite,
  selectExternalSubscription,
  selectStpRates,
  (website, externalSubscription, stpRates): ActivePackageInfo => {
    const forCompanyManagedBillingMode = () => {
      if (website.subscriptionType === AaasSubscriptionType.Website) {
        return {
          name: website.packageName,
          billingInterval: website.billingInterval as SubscriptionTypeEnum,
          touchpoints: website.consumption?.stpLimit ?? 0,
          aaasPackageId: website.packageId,
        };
      } else {
        return {
          name: website.packageName,
          billingInterval: website.billingInterval as SubscriptionTypeEnum,
          touchpoints: stpRates.pool?.limit ?? 0,
          aaasPackageId: website.packageId,
        };
      }
    };

    if (website.billingMode === AaasBillingMode.CompanyManaged) {
      return forCompanyManagedBillingMode();
    }

    /**
     * We can have situations for platform managed billing where the website has an internal 3as subscription, but it doesnt have a corresponding external ( stripe ) subscription.
     * Most usual case is when the website is created with a free plan.
     * Other than this case we always treat the external ( stripe ) subscription as the source of truth
     */
    if (website.billingMode === AaasBillingMode.PlatformManaged) {
      if (externalSubscription) {
        return {
          name: externalSubscription.package.name,
          billingInterval: externalSubscription.package.billingInterval,
          touchpoints: externalSubscription.package.touchpoints,
          aaasPackageId: externalSubscription.package.aaasPackageId,
        };
      } else {
        return forCompanyManagedBillingMode();
      }
    }

    return undefined;
  },
);

/**
 * Returns the total touchpoints that should be included in the current active subscription
 * We make a distinction between this and the STP LIMIT ( only for platform managed billing at the moment ) because those are two values stored in different parts of our infrastructure,
 * and in rare cases could be out of sync. This way we can easily spot any differences in the UI.
 */
export const selectSubscriptionTouchpointsCount = createSelector(
  selectIntpWebsite,
  selectExternalSubscription,
  selectStpRates,
  (website, externalSubscription, stpRates): number => {
    if (website.billingMode === AaasBillingMode.CompanyManaged) {
      switch (website.subscriptionType) {
        case AaasSubscriptionType.Website:
          return stpRates.stpLimit ?? 0;
        case AaasSubscriptionType.Intpc:
          return stpRates.pool?.limit ?? 0;
      }
    }
    if (website.billingMode === AaasBillingMode.PlatformManaged) {
      if (externalSubscription) {
        return externalSubscription.package.touchpoints;
      } else {
        switch (website.subscriptionType) {
          case AaasSubscriptionType.Website:
            return stpRates.stpLimit ?? 0;
          case AaasSubscriptionType.Intpc:
            return stpRates.pool?.limit ?? 0;
        }
      }
    }

    return 0;
  },
);

export const getIntpUpgradeMessage = createSelector(selectIntpWebsite, (website) => {
  const { intpWebsiteId, packageName, packageId, intpCustomerId, inTrial, expiresAt, billingInterval } = website;
  return {
    intpWebsiteId,
    packageName,
    packageId,
    intpCustomerId,
    inTrial,
    expiresAt,
    billingInterval,
  };
});

export const getSeeAllPlansMessage = (state: RootState) => {
  const { intpWebsiteId, intpCustomerId, billingInterval } = selectIntpWebsite(state);
  const consumedCredits = selectStpConsumed(state);
  const recommendedPackage = getPackageWithMinStps(state, consumedCredits, billingInterval as 'monthly' | 'yearly');

  if (!recommendedPackage) return;

  return {
    intpWebsiteId,
    recommendedPackage: {
      name: recommendedPackage.name,
      id: recommendedPackage.id,
    },
    intpCustomerId,
    billingInterval,
  };
};

export const selectPackages = (state: RootState, period: 'monthly' | 'yearly') => {
  const packages: AaasPackage[] = state.get('app').packages[period] ?? [];
  const clonedPackages = [...packages];
  return clonedPackages.sort((a, b) => b.touchpoints - a.touchpoints);
};

/**
 * Finds the first package that meets the minimum number of touchpoints
 */
export const getPackageWithMinStps = (
  state: RootState,
  minTouchpoints: number,
  period: 'monthly' | 'yearly' = 'monthly',
) => {
  const packages: AaasPackage[] = state.get('app').packages[period] ?? [];

  return [...packages].sort((a, b) => a.touchpoints - b.touchpoints).find((p) => p.touchpoints >= minTouchpoints);
};

export const getBillingMode = (state: RootState) => state.get('app')?.intpWebsite?.billingMode as AaasBillingMode;

export const getUniquePackagesByName = (state: RootState, type: 'monthly' | 'yearly') => {
  const packages = state.get('app').packages[type] ?? [];

  return [...packages]
    .sort((a, b) => a.touchpoints - b.touchpoints)
    .filter((pkg, index, self) => index === self.findIndex((p) => p.name === pkg.name));
};

export const selectCurrentPackageName = createSelector(selectActivePackageInfo, (activePackage) => {
  return activePackage?.name;
});

export const getIsFeatureTourEnabled = (state: any) => (state.getIn(['app'])?.theme as AdminTheme).featureTourEnabled;
